import React, { lazy, ReactNode, Suspense } from "react";
import LoadingMask from "../NotWorkArea(Side&Head)/LoadingMask";
import { AxiosRequest } from "../../Url";
import { GetSectionDocs } from "../Tools/Requests";
import { ViewClass } from "../Tools/Consts";

const SectionPersonalData = lazy(() => import("../Sections/ElementsSections/SectionPersonalData/SectionPersonalData"));
const StartPageJsx = lazy(() => import("../Sections/ElementsSections/StartPage/StartPage"));
const Forms = lazy(() => import("../Sections/ElementsSections/Forms/FormsMainFile"));
const DocAndRepo = lazy(() => import("../Sections/SectionReportsAndDocuments"));
const Docs = lazy(() => import("../Sections/ElementsSections/SectionDocuments/SectionDocumentsChose"));
const Reports = lazy(() => import("../Sections/ElementsSections/SectionReport/SectionReports"));
const DView = lazy(() => import("../Sections/ElementsSections/DbView/dbview"));
const SectionNFA = lazy(() => import("../Sections/ElementsSections/NFA/SectionNFA"));
const SectionMultiTab = lazy(() => import("../Sections/ElementsSections/OhersSections/SectionMultiTab"));
const SectionOrganizations = lazy(() => import("../Sections/ElementsSections/Organizations/SectionOrganizations"));
const SectionDocuments = lazy(() => import("../Sections/ElementsSections/SectionDocuments/SectionDocuments"));
const SectionToolsJS = lazy(() => import("../Tools/SectionToolsJS"));
const SectionDepartaments = lazy(() => import("../Sections/ElementsSections/Departament/SectionDepartament"));
const TabsForReports = lazy(() => import("../Sections/ElementsSections/SectionReport/TabsForReports"));
const Params = lazy(() => import("../Sections/ElementsSections/Params/Params"));
const BackgroundProcess = lazy(() => import("../Tools/BackgroundProcess"));
const StillDevelopmentPage = lazy(() => import("./StillDevelopmentPage"));
const Salary = lazy(() => import("./../Sections/ElementsSections/Salary/SalaryComponent"));
const SectionStaff = lazy(() => import("../Sections/ElementsSections/Staff/SectionStaff"));
const Audit = lazy(() => import("../Sections/Audit/UsersAudit"));
const SectionTimeTable = lazy(() => import("../Sections/ElementsSections/TimeTable/SectionTimeTable"));
const AccountsTable = lazy(() => import("../Module/Dialogs/Components/AccountsTable"));
const SectionAccounting = lazy(() => import("../Sections/ElementsSections/Accounting/SectionAccounting"));
const SectionClsBook = lazy(() => import("../Sections/ElementsSections/ClsBook/SectionClsBook"));
const SectionPayTypes = lazy(() => import("../Sections/ElementsSections/PayTypes/SectionPayTypes"));
const SectionWorkTime = lazy(() => import("../Sections/ElementsSections/SectionWorkTime/SectionWorkTime"));
const SectionRecycle = lazy(() => import("../Sections/ElementsSections/Recycle/SectionRecycle"));
const SectionWSChat = lazy(() => import("../Sections/ElementsSections/WSChat/WSChat"));

export interface Section {
  CLSID: string;
  Name?: string;
  ID?: string;
  ObjType?: string;
  Params?: { Path?: string };
  Documents?: Array<object>;
  ViewIdent?: string;
}

interface SectionData {
  element: Section;
  CommitFormOnSection?: any;
  SectionID?: string | number;
}

const getSuspenseForContent = (content: ReactNode | null) => {
  return <Suspense fallback={<LoadingMask />}>{content}</Suspense>;
};

const startPage = () => {
  return (
    <Suspense fallback={<LoadingMask />}>
      <StartPageJsx />
    </Suspense>
  );
};

const dbViewContent = (data: SectionData) => {
  return getSuspenseForContent(<DView CLSID={data.element.CLSID} id={data.element.ID} />);
};

const docsAndReports = (data: SectionData) => {
  return getSuspenseForContent(<DocAndRepo CLSID={data.element.CLSID} id={data.element.ID} />);
};

const forms = (data: SectionData) => {
  return getSuspenseForContent(<Forms id={data.element.ID} CommitFormOnSection={data.CommitFormOnSection} />);
};

const reports = (data: SectionData) => {
  return getSuspenseForContent(
    <Reports CLSID={data.element.CLSID} id={data.element.ID || data.SectionID} SectionToolsJS />,
  );
};

const sectionNFA = (data: SectionData) => {
  return getSuspenseForContent(<SectionNFA sectionData={data.element} SectionID={data.SectionID} />);
};

const backgroundProcess = () => {
  return getSuspenseForContent(<BackgroundProcess />);
};

const params = async (data: SectionData) => {
  const params = new Map();
  params.set("prefix", "programs");
  params.set("comand", "GetParamDialog");
  params.set("GroupID", "0");
  params.set("Path", data.element.Params?.Path);
  params.set("NeedRefresh", "1");
  const json = await AxiosRequest(true, params);

  return getSuspenseForContent(
    <Params
      Module={"programs"}
      reqCommand={"GetParamDialog"}
      GroupID={"0"}
      id={data.element.ID || data.SectionID}
      SectionID={data.element.ID || data.SectionID}
      Path={data.element.Params?.Path}
      data={json}
      NoTitle={true}
      NoHistory={true}
    />,
  );
};

const docs = (data: SectionData) => {
  return getSuspenseForContent(<Docs CLSID={data.element.CLSID} id={data.element.ID} sectionTools={true} />);
};

const wsChat = (data: SectionData) => {
  return getSuspenseForContent(<SectionWSChat secId={data.SectionID} />);
};

const sectionDepartments = (data: SectionData) => {
  return getSuspenseForContent(<SectionDepartaments sectionData={data.element} SectionID={data.SectionID} />);
};

const tabsForReports = (data: SectionData) => {
  return getSuspenseForContent(<TabsForReports reportData={data.element} />);
};

const audit = () => {
  return getSuspenseForContent(<Audit />);
};

const sectionPersonalData = (data: SectionData) => {
  return getSuspenseForContent(<SectionPersonalData sectionData={data.element} SectionID={data.SectionID} />);
};

const docsWithTree = async (data: SectionData) => {
  const sectionId: string | number | undefined = data.SectionID || data.element.ID;
  const objType: string = data.element.ObjType || (await GetSectionDocs(sectionId));

  return getSuspenseForContent(
    <div
      style={{
        height: "100%",
        width: "100%",
      }}
    >
      <SectionDocuments
        id={sectionId}
        DocCfgID={objType}
        HideParams={sectionId && Number(sectionId) > 100}
        SectionToolsJS={sectionId && Number(sectionId) > 100}
        setRecorcCount={() => {}}
        tree={{
          multiCheck: false,
          Module: "documents",
          ObjType: objType,
          SectionID: sectionId,
          ObjRef: "0",
          Info: "0",
          UsedDate: "0",
        }}
        // updateTree={setTree}
      />
    </div>,
  );
};

const accountsTable = (data: SectionData) => {
  return getSuspenseForContent(<AccountsTable props={data.element} />);
};

const sectionOrganizations = (data: SectionData) => {
  return getSuspenseForContent(<SectionOrganizations sectionData={data.element} SectionID={data.SectionID} />);
};

const sectionAccounting = (data: SectionData) => {
  return getSuspenseForContent(
    <SectionAccounting
      CLSID={data.element.CLSID}
      id={data.SectionID || data.element.ID}
      SectionID={data.SectionID || data.element.ID}
      sectionData={data.element}
      element={data.element}
    />,
  );
};

const salary = (data: SectionData) => {
  return getSuspenseForContent(
    <Salary
      CLSID={data.element.CLSID}
      id={data.SectionID || data.element.ID}
      SectionID={data.SectionID || data.element.ID}
      sectionData={data.element}
      element={data.element}
    />,
  );
};

const sectionStaff = (data: SectionData) => {
  return getSuspenseForContent(
    <SectionStaff sectionId={data.SectionID || data.element.ID || -1} CLSID={data.element.CLSID} />,
  );
};

const sectionTimeTable = (data: SectionData) => {
  return getSuspenseForContent(
    <SectionTimeTable sectionId={data.SectionID || data.element.ID || -1} CLSID={data.element.CLSID} />,
  );
};

const sectionMultiTab = (data: SectionData) => {
  return getSuspenseForContent(<SectionMultiTab sectionData={data.element} />);
};

const sectionPayTypes = (data: SectionData) => {
  return getSuspenseForContent(
    <SectionPayTypes SectionID={data.SectionID || data.element.ID} sectionData={data.element} />,
  );
};

const sectionClsBook = (data: SectionData) => {
  return getSuspenseForContent(
    <SectionClsBook SectionID={data.SectionID || data.element.ID} sectionData={data.element} />,
  );
};

const sectionWorkTime = (data: SectionData) => {
  return getSuspenseForContent(
    <SectionWorkTime SectionID={data.SectionID || data.element.ID} sectionData={data.element} />,
  );
};

const sectionRecycle = (data: SectionData) => {
  return getSuspenseForContent(<SectionRecycle sectionId={data.SectionID || data.element.ID || -1} />);
};

const stillDevelopmentPage = (data: SectionData) => {
  return getSuspenseForContent(<StillDevelopmentPage SectionToolsJS={true} id={data.SectionID || data.element.ID} />);
};

const defaultContent = (data: SectionData) => {
  if (data.element.Documents) {
    return docsAndReports(data);
  }

  return stillDevelopmentPage(data);
};

export async function returnPageBasedOnCLSID(element: Section, CommitFormOnSection?: any, SectionID?: string) {
  const data: SectionData = {
    element,
    CommitFormOnSection,
    SectionID,
  };

  const content: { [key: string]: (data: SectionData) => React.JSX.Element | Promise<React.JSX.Element> } = {
    [ViewClass.DBView]: dbViewContent,
    [ViewClass.DocReps]: docsAndReports,
    "{1D3BB51B-8729-4F3F-91F3-574898BFA297}": docsAndReports,
    "{DB69A304-4DED-46F6-8BD3-BADF1BB24393}": docsAndReports,
    [ViewClass.HTMLView]: startPage,
    [ViewClass.Forms]: forms,
    [ViewClass.RepSite]: reports,
    [ViewClass.StgBook]: sectionNFA,
    [ViewClass.LazyStat]: backgroundProcess,
    [ViewClass.ParamDlg]: params,
    [ViewClass.DocSite]: docs,
    [ViewClass.Departs]: sectionDepartments,
    [ViewClass.CellObj]: tabsForReports,
    [ViewClass.Audit]: audit,
    [ViewClass.Persons]: sectionPersonalData,
    [ViewClass.DocBook]: docsWithTree,
    [ViewClass.Transact]: accountsTable,
    [ViewClass.OrgBook]: sectionOrganizations,
    [ViewClass.Account]: sectionAccounting,
    [ViewClass.Salary]: salary,
    [ViewClass.Staff]: sectionStaff,
    [ViewClass.TimeTable]: sectionTimeTable,
    [ViewClass.MultiTab]: sectionMultiTab,
    [ViewClass.PayTypes]: sectionPayTypes,
    [ViewClass.ClsBook]: sectionClsBook,
    [ViewClass.WModes]: sectionWorkTime,
    [ViewClass.Recycle]: sectionRecycle,
    [ViewClass.WSChat]: wsChat,
  };

  return content[element.CLSID]?.(data) || defaultContent(data);
}

export async function contentMaker(sideBarSelected: any, CommitFormOnSection?: any) {
  //
  let content;
  if (sideBarSelected.ID && sideBarSelected.CLSID === ViewClass.MultiTab) {
    content = (
      <Suspense fallback={<LoadingMask />}>
        <SectionMultiTab sectionData={sideBarSelected} />
      </Suspense>
    );
  } else if (sideBarSelected.ID !== undefined) {
    content = await returnPageBasedOnCLSID(sideBarSelected, CommitFormOnSection);
  }
  return content;
}
